import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import BlueButton from "../../../components/BlueButton";
import WhiteButton from "../../../components/WhiteButton";
import { CardSecao, RodapeFixo } from "../../../components/PadraoTela";
import BreadcrumbApuracao from "../../Apuracoes/components/BreadcrumbApuracao";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams } from "react-router-dom";
import SnackBar from "../../../components/SnackBar";
import BadgeStatus from "../../Apuracoes/components/BadgeStatus";
import { formatarCnpj, fmtMesAno } from "../../Apuracoes/utils";
import { formatarMoeda } from "../../Apuracoes/Detalhe/utils/agregarPorConta.util";
import { buscarProjeto, executarAcaoProjeto } from "../../../services/projetos-apuracao.service";
import {
  usePermissoes,
  CHAVES_APURACOES_EDIT,
  CHAVES_PIS_COFINS_APPROVE,
} from "../../../config/Context/PermissoesContext";
import {
  STATUS_LABEL,
  TIPO_LABEL,
  acoesDisponiveis,
  formatarData,
  formatarDataHora,
  rotulosDoTipo,
  totalDoGrupo,
} from "../utils/projeto.util";
import { montarUrlApuracao } from "../utils/url.util";
import ModalAcaoProjeto from "./ModalAcaoProjeto";
import styles from "./styles.module.css";

const ROTULO_ACAO = {
  "atualizar-valores": "Atualizar valores",
  enviar: "Enviar Projeto ao Cliente",
  liberar: "Liberar Prévia",
  cancelar: "Cancelar Projeto",
  aprovar: "Aprovar Projeto",
};

const ROTULO_ACAO_EM_ANDAMENTO = {
  "atualizar-valores": "Atualizando…",
  "reenviar-creditos": "Reenviando…",
};

// Ações que exigem confirmação num modal; as demais executam direto.
const ACOES_COM_MODAL = ["enviar", "liberar", "cancelar", "aprovar"];

const TOM_POR_STATUS = { APROVADO: "ok", ENVIADO: "alerta" };

function periodoLabel(periodo) {
  if (!periodo?.inicio) return "—";
  return periodo.inicio === periodo.fim
    ? fmtMesAno(periodo.inicio)
    : `${fmtMesAno(periodo.inicio)} – ${fmtMesAno(periodo.fim)}`;
}

function textoCreditosPendentes(quantidade) {
  return quantidade === 1
    ? "1 crédito não foi lançado na Conta Corrente"
    : `${quantidade} créditos não foram lançados na Conta Corrente`;
}

function textoItensRemovidos(quantidade) {
  return quantidade === 1
    ? "1 item saiu do projeto (já utilizado ou sem base legal)."
    : `${quantidade} itens saíram do projeto (já utilizados ou sem base legal).`;
}

function CardTotais({ nome, destaque = false, linhas, valor }) {
  return (
    <section aria-label={nome} className={`${styles.card} ${destaque ? styles.cardDestaque : ""}`}>
      <div className={styles.cardLabel}>{nome}</div>
      {valor !== undefined && <div className={styles.cardValor}>{formatarMoeda(valor)}</div>}
      {linhas?.map(({ rotulo, valor: v, total }) => (
        <div key={rotulo} className={`${styles.cardLinha} ${total ? styles.cardLinhaTotal : ""}`}>
          <span>{rotulo}</span>
          <span>{formatarMoeda(v)}</span>
        </div>
      ))}
    </section>
  );
}

function mensagemDaAcao(acao, projeto, res) {
  switch (acao) {
    case "enviar":
      return { type: "success", msg: `Projeto ${projeto.codigo} enviado ao cliente.` };
    case "liberar":
      return { type: "success", msg: `Projeto ${projeto.codigo} liberado: voltou para Em Prévia.` };
    case "cancelar":
      return { type: "success", msg: `Projeto ${projeto.codigo} cancelado.` };
    case "aprovar": {
      const pendentes = res?.creditos?.pendentes ?? 0;
      if (pendentes > 0) {
        return {
          type: "warning",
          msg: `Projeto ${projeto.codigo} aprovado, mas ${textoCreditosPendentes(pendentes)}. Use "Reenviar créditos".`,
        };
      }
      return { type: "success", msg: `Projeto ${projeto.codigo} aprovado!` };
    }
    case "atualizar-valores": {
      const removidos = Array.isArray(res?.removidos) ? res.removidos.length : 0;
      return {
        type: removidos > 0 ? "warning" : "success",
        msg: `Valores atualizados.${removidos > 0 ? ` ${textoItensRemovidos(removidos)}` : ""}`,
      };
    }
    case "reenviar-creditos": {
      const pendentes = res?.creditos?.pendentes ?? 0;
      return pendentes > 0
        ? { type: "warning", msg: `Ainda há ${pendentes} ${pendentes === 1 ? "crédito pendente" : "créditos pendentes"} de lançamento.` }
        : { type: "success", msg: "Créditos lançados na Conta Corrente." };
    }
    default:
      return { type: "success", msg: "Ação concluída." };
  }
}

/**
 * TXM-558 — Resumo do Projeto: dados, totais (Administrativo/Judicial), oportunidades e as ações
 * por status. Aprovar é definitivo; falha de crédito não desfaz a aprovação e pode ser reenviada.
 */
export default function ResumoProjetoApuracao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeEditar = pode(CHAVES_APURACOES_EDIT);
  const podeAprovar = pode(CHAVES_PIS_COFINS_APPROVE);

  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(true);
  const [acaoEmModal, setAcaoEmModal] = useState(null);
  const [acaoEmAndamento, setAcaoEmAndamento] = useState(null);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const carregar = useCallback(async () => {
    const res = await buscarProjeto(id);
    if (!res?.sucesso) {
      setErro(res?.mensagem || "Não foi possível carregar o projeto.");
      setDados(null);
    } else {
      setErro("");
      setDados(res);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    carregar();
  }, [carregar]);

  const executar = async (acao) => {
    if (!dados) return;
    setAcaoEmAndamento(acao);
    const res = await executarAcaoProjeto(dados.projeto.idProjeto, acao);
    setAcaoEmAndamento(null);

    if (!res?.sucesso) {
      setAcaoEmModal(null);
      setObjSnackBar({ type: "error", msg: res?.mensagem || "Não foi possível executar a ação." });
      return;
    }

    setAcaoEmModal(null);
    setObjSnackBar(mensagemDaAcao(acao, dados.projeto, res));
    await carregar();
  };

  const acoes = useMemo(
    () => (dados ? acoesDisponiveis(dados.projeto.status, { podeEditar, podeAprovar }) : []),
    [dados, podeEditar, podeAprovar]
  );

  const voltar = () => navigate("/conta-corrente/projetos");

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <GridLoader color="#18316e" />
      </Box>
    );
  }

  if (erro || !dados) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro || "Não foi possível carregar o projeto."}</Alert>
        <Button onClick={voltar} sx={{ mt: 2, textTransform: "none" }}>
          ← Voltar para Projetos
        </Button>
      </Box>
    );
  }

  const { projeto, totais, oportunidades, creditos } = dados;
  const rotulos = rotulosDoTipo(projeto.tipo);
  const ocupado = Boolean(acaoEmAndamento);
  const creditosPendentes = projeto.status === "APROVADO" ? creditos?.pendentes ?? 0 : 0;
  const creditosComErro = (creditos?.itens || []).filter((item) => item.statusEnvio !== "enviado");

  const totalOportunidades = oportunidades.length;

  return (
    <>
      <BreadcrumbApuracao
        itens={[
          { label: "Conta Corrente", onClick: () => navigate("/conta-corrente/extrato") },
          { label: "Projetos", onClick: voltar },
          { label: "Resumo do Projeto" },
        ]}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.5 }}>
          <AssignmentOutlinedIcon sx={{ color: "#18316e", fontSize: 28 }} />
          <Typography
            component="h1"
            variant="h5"
            fontWeight={700}
            sx={{ color: "#18316e", fontSize: { xs: "1.25rem", md: "1.625rem" }, letterSpacing: "-0.3px" }}
          >
            Resumo do Projeto
          </Typography>
        </Box>

        <CardSecao icone={<AssignmentOutlinedIcon />} titulo="Dados do Projeto">
          <section aria-label="Dados do projeto" className={styles.infoBar}>
            {[
              ["ID do Projeto", projeto.codigo],
              ["Empresa", projeto.razaoSocial],
              ["CNPJ", formatarCnpj(projeto.cnpj)],
              ["Tipo", TIPO_LABEL[projeto.tipo] || projeto.tipo],
              ["Período", periodoLabel(projeto.periodo)],
            ].map(([rotulo, valor]) => (
              <div key={rotulo}>
                <div className={styles.infoLabel}>{rotulo}</div>
                <div className={styles.infoValue}>{valor}</div>
              </div>
            ))}
            <div>
              <div className={styles.infoLabel}>Status</div>
              <div className={styles.infoValue}>
                <BadgeStatus tom={TOM_POR_STATUS[projeto.status] || "neutro"}>
                  {STATUS_LABEL[projeto.status] || projeto.status}
                </BadgeStatus>
              </div>
            </div>
          </section>
          <p className={styles.infoLabel} style={{ marginTop: 16, marginBottom: 0 }}>
            Valores processados em {formatarDataHora(projeto.dataProcessamento)}
          </p>
        </CardSecao>

        {projeto.status === "ENVIADO" && (
          <div className={`${styles.banner} ${styles.bannerEnviado}`}>
            <span>Projeto enviado ao cliente. Aguardando aprovação.</span>
            <span className={styles.bannerData}>Enviado em {formatarData(projeto.dtEnvio)}</span>
          </div>
        )}
        {projeto.status === "CANCELADO" && (
          <div className={`${styles.banner} ${styles.bannerCancelado}`}>
            <span>Projeto cancelado. É possível reenviá-lo.</span>
            <span className={styles.bannerData}>Cancelado em {formatarData(projeto.dtCancelamento)}</span>
          </div>
        )}
        {projeto.status === "APROVADO" && (
          <div className={`${styles.banner} ${styles.bannerAprovado}`}>
            <span>Projeto aprovado. Os itens constam como utilizados nas apurações.</span>
            <span className={styles.bannerData}>Aprovado em {formatarData(projeto.dtAprovacao)}</span>
          </div>
        )}

        {projeto.apuracaoDesatualizada && projeto.status !== "APROVADO" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            A apuração foi reprocessada depois deste cálculo, então os valores podem estar
            desatualizados.
            {projeto.status === "EM_PREVIA" ? ' Use "Atualizar valores".' : ""}
          </Alert>
        )}

        {creditosPendentes > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              podeAprovar ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={ocupado}
                  onClick={() => executar("reenviar-creditos")}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {acaoEmAndamento === "reenviar-creditos"
                    ? ROTULO_ACAO_EM_ANDAMENTO["reenviar-creditos"]
                    : "Reenviar créditos"}
                </Button>
              ) : null
            }
          >
            {textoCreditosPendentes(creditosPendentes)}. A aprovação foi mantida; os valores são os
            mesmos e o reenvio não duplica o crédito.
            {creditosComErro.length > 0 && (
              <ul className={styles.creditos}>
                {creditosComErro.map((credito) => (
                  <li key={`${credito.mesReferencia}-${credito.tipoCredito}`} className={styles.creditoErro}>
                    {fmtMesAno(credito.mesReferencia)} · {String(credito.tipoCredito).toUpperCase()} ·{" "}
                    {formatarMoeda(credito.valor)}
                    {credito.erroMensagem ? ` — ${credito.erroMensagem}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <CardSecao icone={<BarChartIcon />} titulo="Totais do Projeto">
          <div className={styles.cards} style={{ marginBottom: 0 }}>
            <CardTotais nome="Total" destaque valor={totais.total} />
            <CardTotais nome={rotulos.a} valor={totais.pis} />
            <CardTotais nome={rotulos.b} valor={totais.cofins} />
          </div>
        </CardSecao>

        <CardSecao icone={<AccountBalanceOutlinedIcon />} titulo="Distribuição por Tipo de Crédito">
          <div className={styles.cards} style={{ marginBottom: 0 }}>
            <CardTotais
              nome="Administrativo"
              linhas={[
                { rotulo: rotulos.a, valor: totais.administrativo.pis },
                { rotulo: rotulos.b, valor: totais.administrativo.cofins },
                {
                  rotulo: "Total",
                  valor: totalDoGrupo(totais.administrativo),
                  total: true,
                },
              ]}
            />
            <CardTotais
              nome="Judicial"
              linhas={[
                { rotulo: rotulos.a, valor: totais.judicial.pis },
                { rotulo: rotulos.b, valor: totais.judicial.cofins },
                { rotulo: "Total", valor: totalDoGrupo(totais.judicial), total: true },
              ]}
            />
          </div>
        </CardSecao>

        <CardSecao icone={<ListAltOutlinedIcon />} titulo="Oportunidades Identificadas" corpoSx={{ p: 0 }}>
          <div className={styles.tableWrap} style={{ border: "none", borderRadius: 0 }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Oportunidade</th>
                  <th>Período Apurado</th>
                  <th>Fundamento Legal / Tese Judicial</th>
                  <th>Tipo</th>
                  <th className={styles.direita}>{rotulos.a} (R$)</th>
                  <th className={styles.direita}>{rotulos.b} (R$)</th>
                  <th className={styles.direita}>Total (R$)</th>
                </tr>
              </thead>
              <tbody>
                {oportunidades.map((oportunidade) => (
                  <tr key={`${oportunidade.baseLegalId}-${oportunidade.nome}`}>
                    <td>{oportunidade.nome}</td>
                    <td>{periodoLabel(oportunidade.periodo)}</td>
                    <td>{oportunidade.fundamentacao || "—"}</td>
                    <td>
                      <span
                        className={`${styles.tipoBadge} ${
                          oportunidade.tipo === "judicial" ? styles.tipoJudicial : styles.tipoAdministrativo
                        }`}
                      >
                        {oportunidade.tipo === "judicial" ? "Judicial" : "Administrativo"}
                      </span>
                    </td>
                    <td className={styles.direita}>{formatarMoeda(oportunidade.pis)}</td>
                    <td className={styles.direita}>{formatarMoeda(oportunidade.cofins)}</td>
                    <td className={styles.direita}>{formatarMoeda(totalDoGrupo(oportunidade))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Total Geral</td>
                  <td className={styles.direita}>{formatarMoeda(totais.pis)}</td>
                  <td className={styles.direita}>{formatarMoeda(totais.cofins)}</td>
                  <td className={styles.direita}>{formatarMoeda(totais.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardSecao>

        <RodapeFixo
          resumo={
            <>
              <Chip
                icon={<ListAltOutlinedIcon sx={{ fontSize: 18 }} />}
                label={`${totalOportunidades} ${totalOportunidades === 1 ? "oportunidade" : "oportunidades"}`}
                size="small"
                sx={{ bgcolor: "#e3f2fd", color: "#1565c0" }}
              />
              <Chip
                icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                label={`Total: ${formatarMoeda(totais.total)}`}
                size="small"
                sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
              />
            </>
          }
        >
          <WhiteButton
            textValue="← Voltar para Projetos"
            clickFunction={ocupado ? undefined : voltar}
            customStyle={ocupado ? { opacity: 0.55, pointerEvents: "none" } : undefined}
          />
          <WhiteButton
            textValue="Ver Apuração"
            clickFunction={ocupado ? undefined : () => navigate(montarUrlApuracao(projeto))}
            customStyle={ocupado ? { opacity: 0.55, pointerEvents: "none" } : undefined}
          />
          {acoes.map((acao) => (
            <BlueButton
              key={acao}
              disabled={ocupado}
              clickFunction={() => (ACOES_COM_MODAL.includes(acao) ? setAcaoEmModal(acao) : executar(acao))}
              textValue={
                acaoEmAndamento === acao && ROTULO_ACAO_EM_ANDAMENTO[acao]
                  ? ROTULO_ACAO_EM_ANDAMENTO[acao]
                  : ROTULO_ACAO[acao]
              }
            />
          ))}
        </RodapeFixo>
      </BreadcrumbApuracao>

      <ModalAcaoProjeto
        open={Boolean(acaoEmModal)}
        acao={acaoEmModal}
        projeto={projeto}
        executando={ocupado}
        onConfirmar={() => executar(acaoEmModal)}
        onClose={() => setAcaoEmModal(null)}
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
}
